import React, { useCallback, useEffect, useState } from "react";
import { ImageOff, Landmark, Search } from "lucide-react";
import { SelectField } from "../components/ui/Field";
import { Chip } from "../components/ui/Badge";
import { Alert, EmptyState, SearchingState } from "../components/ui/Feedback";
import Pagination from "../components/results/Pagination";
import IndianTrademarkDetailModal from "../components/indian-trademarks/IndianTrademarkDetailModal";
import { indianTrademarkApi } from "../api/indianTrademarkApi";
import { humanize } from "../lib/format";

const EM_DASH = "—";

/**
 * Browses the `trademarkdata` collection — Indian Trade Marks Journal
 * entries imported directly into MongoDB, not fetched from EUIPO/TMview.
 * Read-only: this page only lists and searches it, it never writes.
 */
export default function IndianTrademarkDataPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [trademarkClass, setTrademarkClass] = useState("");
  const [markType, setMarkType] = useState("");

  const [state, setState] = useState({
    loading: true,
    items: [],
    total: 0,
    error: null,
    dbOffline: false,
  });
  const [facets, setFacets] = useState({
    jurisdictions: [],
    classes: [],
    markTypes: [],
  });
  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await indianTrademarkApi.list({
        page,
        limit: pageSize,
        search: search || undefined,
        jurisdiction: jurisdiction || undefined,
        class: trademarkClass || undefined,
        markType: markType || undefined,
      });
      setState({
        loading: false,
        items: data.items || [],
        total: data.total || 0,
        error: null,
        dbOffline: Boolean(data.dbOffline),
      });
      if (data.facets) setFacets(data.facets);
    } catch (err) {
      setState({
        loading: false,
        items: [],
        total: 0,
        error: err.message,
        dbOffline: false,
      });
    }
  }, [page, pageSize, search, jurisdiction, trademarkClass, markType]);

  useEffect(() => {
    load();
  }, [load]);

  function handleSearchSubmit(event) {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  }

  function handleFilterChange(setter) {
    return (event) => {
      setPage(1);
      setter(event.target.value);
    };
  }

  const hasFilters = Boolean(
    search || jurisdiction || trademarkClass || markType,
  );

  function clearFilters() {
    setSearchInput("");
    setSearch("");
    setJurisdiction("");
    setTrademarkClass("");
    setMarkType("");
    setPage(1);
  }

  return (
    <div className="mx-auto max-w-[1300px] px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded bg-[linear-gradient(135deg,#8f5eeb,#7a54e5)]">
          <Landmark
            className="h-4 w-4 text-white"
            strokeWidth={2.25}
            aria-hidden="true"
          />
        </span>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-ink-900">
            Indian Trade Marks Journal
          </h1>
          <p className="text-sm text-ink-600">
            Entries imported from the Journal — searchable by application,
            class, jurisdiction and mark type.
          </p>
        </div>
      </div>

      <div className="mt-6 rounded-soft border border-ink-200 bg-white p-4 shadow-raised">
        <form
          onSubmit={handleSearchSubmit}
          className="flex flex-wrap items-end gap-3"
        >
          <div className="min-w-[240px] flex-1">
            <label htmlFor="itd-search" className="field-label">
              Search
            </label>
            <div className="relative">
              <input
                id="itd-search"
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Application number, mark text, goods & services…"
                className="control h-10 pl-9"
              />
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
                aria-hidden="true"
              />
            </div>
          </div>

          <SelectField
            label="Jurisdiction"
            value={jurisdiction}
            onChange={handleFilterChange(setJurisdiction)}
            className="w-44"
            options={[
              { value: "", label: "Any jurisdiction" },
              ...facets.jurisdictions.map((j) => ({ value: j, label: j })),
            ]}
          />

          <SelectField
            label="Class"
            value={trademarkClass}
            onChange={handleFilterChange(setTrademarkClass)}
            className="w-32"
            options={[
              { value: "", label: "Any class" },
              ...facets.classes.map((c) => ({
                value: String(c),
                label: `Class ${c}`,
              })),
            ]}
          />

          <SelectField
            label="Mark type"
            value={markType}
            onChange={handleFilterChange(setMarkType)}
            className="w-40"
            options={[
              { value: "", label: "Any type" },
              ...facets.markTypes.map((t) => ({
                value: t,
                label: humanize(t),
              })),
            ]}
          />

          <button
            type="submit"
            className="h-10 rounded bg-ink-900 px-5 text-sm font-semibold text-white hover:bg-ink-800"
          >
            Search
          </button>
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="h-10 text-sm font-medium text-ink-600 underline underline-offset-2 hover:text-ink-900"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {state.dbOffline && (
        <div className="mt-5">
          <Alert tone="error">
            The database isn't connected, so journal data can't be loaded right
            now.
          </Alert>
        </div>
      )}
      {state.error && !state.dbOffline && (
        <div className="mt-5">
          <Alert tone="error">{state.error}</Alert>
        </div>
      )}

      <div className="mt-5">
        {state.loading ? (
          <SearchingState note="Loading journal entries…" />
        ) : state.items.length === 0 ? (
          <EmptyState title="No entries found">
            {hasFilters
              ? "Nothing matches these filters — try clearing one."
              : "The journal data collection is empty."}
          </EmptyState>
        ) : (
          <div className="overflow-hidden rounded-soft border border-ink-200 bg-white shadow-raised">
            <div className="divide-y divide-ink-100">
              {state.items.map((row) => (
                <button
                  key={row._id}
                  type="button"
                  onClick={() => setOpenRow(row)}
                  className="flex w-full items-start gap-4 p-4 text-left hover:bg-paper"
                >
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded border border-ink-200 bg-paper">
                    {row.logo ? (
                      <img
                        src={indianTrademarkApi.logoUrl(row._id)}
                        alt={row.markText || "Mark"}
                        className="h-full w-full object-contain p-1"
                        loading="lazy"
                      />
                    ) : (
                      <ImageOff
                        className="h-4 w-4 text-ink-300"
                        aria-hidden="true"
                      />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-sm font-semibold text-ink-900">
                        {row.markText || row.markType || "Device mark"}
                      </span>
                      <Chip>App {row.applicationNumber}</Chip>
                      <Chip>Class {row.class ?? EM_DASH}</Chip>
                      {row.jurisdiction && <Chip>{row.jurisdiction}</Chip>}
                    </div>
                    <p className="mt-1 line-clamp-1 text-xs text-ink-600">
                      {row.goodsDescription}
                    </p>
                    <p className="mt-1 text-micro text-ink-400">
                      Filed {row.dateOfFiling || EM_DASH} · Used since{" "}
                      {row.usedSince || EM_DASH}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            <Pagination
              page={page}
              pageSize={pageSize}
              total={state.total}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>

      {openRow && (
        <IndianTrademarkDetailModal
          row={openRow}
          onClose={() => setOpenRow(null)}
        />
      )}
    </div>
  );
}
